import lodash from 'lodash';
import Fuse from 'fuse.js/dist/fuse';
import { addApplicationMessage } from 'common/actions/applicationMessagesActions';
import { mutateGraphQL, queryGraphQL } from 'common/util/query';
import { node, nodeWithArgs, toMutation, toQuery } from 'common/util/queryHelper';
import { userDefaultInstitutionIdSelector } from 'common/selectors';
import { loadDataSourceItems } from 'common/actions/dataSources';
import { browserHistory } from 'react-router';
import { fuzzyMatchOptions } from 'common/util/fuse/fuse';

import { formatMetadataValue } from './editStudy';
import { jsonHelper, QueryNodeRegistry } from '@complion/query';

import { delegationsList, PRINCIPAL_INVESTIGATOR_ROLE } from 'pages/delegationOfAuthority/selectors';
import { buildAddStudyDelegationV2Query } from 'pages/delegationOfAuthority/v2/actions/studyDelegationsV2';
import { delegationsUnderPITenurePredicate } from 'pages/delegationOfAuthority/v2/util/predicates';
import { doaFeatureAccess } from 'common/features/predicates';
import uuid from 'node-uuid';
import { getPredicateGroups } from 'binder/util/PredicateGroupingVisitor';
import { STUDY_USER_TYPE } from 'components/common/Study/StudyTeamMemberForm';
import moment from 'moment';
import { convertToISOString } from 'common/util/dateTime';

export const LOAD_INSTITUTION_STUDY_TYPES = 'LOAD_INSTITUTION_STUDY_TYPES';
export const LOAD_INSTITUTION_STUDY_TEAM_MEMBERS = 'LOAD_INSTITUTION_STUDY_TEAM_MEMBERS';
export const LOAD_INSTITUTION_STUDY_ROLES = 'LOAD_INSTITUTION_STUDY_ROLES';
export const LOAD_STUDY_METADATA = 'LOAD_STUDY_METADATA';
export const LOAD_STUDY_STAGES = 'LOAD_STUDY_STAGES';
export const SAVE_STUDY_DATA = 'SAVE_STUDY_DATA';
export const CLEAR_STUDY = 'CLEAR_STUDY';

export const LOADING_STUDY_STATE = 'LOADING_STUDY_STATE';
export const STUDY_STATE_LOADED = 'STUDY_STATE_LOADED';
export const SET_STUDY_TEAM_POTENTIAL_MATCH = 'SET_STUDY_TEAM_POTENTIAL_MATCH';
export const SAVING_STUDY_STATE = 'SAVING_STUDY_STATE';
export const STUDY_STATE_SAVED = 'STUDY_STATE_SAVED';

const TYPE = {
    'essentials': 'studyEssentials',
    'binder': 'studyDetails'
};

const parseDateFormat = (date) => {
    if (lodash.isNil(date)) {
        return null;
    }

    return moment.utc(date);
};

export function getStudyTypesByInstitution(institutionId) {
    return (dispatch) => {
        const query = nodeWithArgs('studyTypes', {
            institutionId,
        }, [
            'id',
            'name',
            'active',
            'binderTemplateId'
        ]);

        return queryGraphQL(toQuery(query)).then(results => {
            if (results && results.errors) {
                dispatch(addApplicationMessage('error', 'Failed to fetch study types'));
            } else {
                dispatch({
                    type: LOAD_INSTITUTION_STUDY_TYPES,
                    payload: results.data.studyTypes,
                });
            }
        });
    };
}

export function getStudyRolesByInstitution(institutionId) {
    return async dispatch => {
        const query = nodeWithArgs('rolesManagementForInstitution', {
            institutionId,
        }, [
            'id',
            'name',
            'isSystem',
            'active',
        ]);

        const response = await queryGraphQL(toQuery(query));
        if (response && response.errors) {
            dispatch(addApplicationMessage('error', 'Failed to fetch study roles.'));
        } else {
            const studyRoles = response.data.rolesManagementForInstitution;
            const nonSystemRoles = lodash
                .filter(studyRoles, role => role.isSystem === false)
                .sort((role1, role2) => lodash.get(role1, 'name', '').localeCompare(lodash.get(role2, 'name', '')));
            const systemRoles = lodash
                .filter(studyRoles, role => role.isSystem === true)
                .sort((role1, role2) => lodash.get(role1, 'name', '').localeCompare(lodash.get(role2, 'name', '')));
            const newStudyRoles = lodash.concat(systemRoles, nonSystemRoles);

            dispatch({
                type: LOAD_INSTITUTION_STUDY_ROLES,
                payload: newStudyRoles,
            });
        }
    };
}

const getStudyEssentialsByStudyTypeId = (institutionId, studyTypeId) => {
    const query = nodeWithArgs('studyDescriptors', {
        institutionId,
        studyTypeId
    }, [
        'id',
        'studyTypeId',
        'name',
        'descriptorId',
        'institutionId',
        'dataSourceId',
        node('dataSources', [
            'value',
            'text',
            'dataType',
            'order'
        ]),
        'inputFieldType',
        'dataType',
        'type'
    ]);

    return queryGraphQL(toQuery(query));
};


export function getTeamMembersByInstitution(institutionId) {
    return async dispatch => {

        const query = nodeWithArgs('createStudyGetUsers', {
            institutionId,
            sortField: 'displayName',
            filterField: true,
        }, [
            'id',
            'sub',
            'displayName',
        ]);

        const results = await queryGraphQL(toQuery(query));
        if (results && results.errors) {
            dispatch(addApplicationMessage('error', 'Failed to fetch study team members'));
        } else {
            dispatch({
                type: LOAD_INSTITUTION_STUDY_TEAM_MEMBERS,
                payload: results.data.createStudyGetUsers
            });
        }
    };
}

export function setPotentialResearchersToStudyTeam(studyTeamMembers) {
    return async (dispatch, getState) => {
        const state = getState();
        const staffDescriptorDataSources = lodash.get(state, 'study.staffDescriptor.dataSources', null);

        if (!staffDescriptorDataSources) {
            return;
        }

        const fuzzyMatch = new Fuse(staffDescriptorDataSources, fuzzyMatchOptions);
        const updatedTeamMembers = studyTeamMembers.map((teamMember) => {
            const potentialMatch = fuzzyMatch.search(teamMember.displayName);
            lodash.set(teamMember, 'potentialMatch', potentialMatch);

            return teamMember;
        });

        dispatch({
            type: SET_STUDY_TEAM_POTENTIAL_MATCH,
            payload: updatedTeamMembers,
        });
    };
}

export function saveStudy(status, nextLocation) {
    return async (dispatch, getState) => {
        const storeState = getState();

        let originalStudyId = lodash.get(storeState, 'study.originalFields.studyId', null);
        let studyId = originalStudyId;
        dispatch(savingStudyStateAction());

        if (!originalStudyId) {
            const studyResult = await createStudyMutation(storeState);
            validateResolvedMutation(studyResult, 'type and name', dispatch);
            studyId = lodash.get(studyResult, 'data.createStudy.id', null);
        }

        createStudyTeamMembers(storeState, studyId, dispatch);
        createStudyMetadata(storeState, studyId, 'essentials', dispatch);
        createStudyMetadata(storeState, studyId, 'binder', dispatch);

        // If saving as draft, shortcut study binder creation
        if (status === 'draft') {
            return browserHistory.push(nextLocation || '/studies');
        }

        await activateStudy(storeState, studyId, dispatch);
        dispatch(studyStateSavedAction());
        browserHistory.push(`/study/${studyId}`);
    };
}

export const createDelegationsV2 = async ({ institutionId, binderId, studyId, users, state, dispatch }) => {
    const isDoaEnabled = doaFeatureAccess(state, institutionId);

    if (isDoaEnabled && !lodash.isEmpty(users)) {
        const delegations = [];
        for (const user of users) {
            const signatures = [];
            if (user.studyRoleName === PRINCIPAL_INVESTIGATOR_ROLE) {
                const currentDOA = delegationsList(state);
                const staffDelegationsOverlappingWithNewPITenure = delegationsUnderPITenurePredicate(currentDOA, [
                    new Date(),
                    null,
                ]);

                const signaturesForNewPI = lodash.map(staffDelegationsOverlappingWithNewPITenure, (delegation) => ({
                    delegationId: delegation.id,
                    user: {
                        id: user.id,
                        name: user.name,
                    },
                    signatureType: 'PI_START',
                }));

                signatures.push(...signaturesForNewPI);
            }
            const startDate = parseDateFormat(moment().format('LL'));

            delegations.push({
                delegation: {
                    id: uuid.v4(),
                    binderId,
                    studyId,
                    startDate: convertToISOString(startDate),
                    user: {
                        id: user.id,
                        name: user.name
                    },
                    roleManagementId: user.studyRoleId,
                    auditOrigin: 'Study Settings',
                    auditActivity: 'A corresponding delegation is created when a Study Team Member is added in Study Settings.'

                },
                signatureExpectations: signatures,
                defaultSignatureExpectations: signatures,
            });
        }

        const query = buildAddStudyDelegationV2Query({ delegations, institutionId, studyId });
        const response = await mutateGraphQL(toMutation(query));
        validateResolvedMutation(response, 'createDelegationsV2', dispatch);
        return response.data.createStudyDelegationV2;
    }
    return [];
};

function validateResolvedMutation(output, category, dispatch) {
    if (output && output.errors) {
        dispatch(addApplicationMessage('error', `An error occurred when saving study ${category} to the study.`));
    }
}

export async function activateStudy(state, studyId, dispatch) {
    const studyBinderResult = await createStudyBinder(state, studyId);

    validateResolvedMutation(studyBinderResult, 'binder', dispatch);

    const binderId = lodash.get(studyBinderResult, 'data.createStudyBinder');
    const updateStudyResult = await updateStudy(state, studyId, binderId);
    validateResolvedMutation(updateStudyResult, '', dispatch);

    const studyTeamBinderAccessResult = await setStudyTeamBinderAccess(state, binderId);
    validateResolvedMutation(studyTeamBinderAccessResult, 'team members', dispatch);

    const studyTeam = lodash.get(state, 'study.fields.studyTeam', []);
    const teamMembers = studyTeam.map(teamMember => ({
        id: teamMember.userId,
        name: teamMember.nameOfResearcher,
        studyRoleId: teamMember.studyRoleId
    }));
    const institutionId = userDefaultInstitutionIdSelector(state);

    await createDelegationsV2({
        institutionId, binderId, studyId, users: teamMembers, state, dispatch
    });

    dispatch(addApplicationMessage('success', 'Study activated.'));
    dispatch({
        type: SAVE_STUDY_DATA,
    });
    return binderId;
}

export function getStudyMetadata(institutionId, binderTemplateId, studyTypeId) {
    return async dispatch => {
        dispatch(loadingStudyStateAction());
        const results = await getBinderTemplateRequirements(institutionId, binderTemplateId);
        if (results && results.errors || binderTemplate === null) {
            return dispatch(addApplicationMessage('error', 'Failed to load all study details'));
        }

        const binderTemplate = results.data.me.binderTemplate;
        const descriptors = results.data.descriptors;
        const deserializedObj = jsonHelper.parse(binderTemplate.rootQuery);
        binderTemplate.rootQuery = QueryNodeRegistry.reconstituteDeserializedJson(deserializedObj);
        binderTemplate.predicateGroups = await getPredicateGroups(binderTemplate.rootQuery);

        const dataSourceIds = getDataSourceIdsFromBinderTemplate(binderTemplate);
        dispatch(loadDataSourceItems(dataSourceIds));

        const studyEssentialResults = await getStudyEssentialsByStudyTypeId(institutionId, studyTypeId);
        if (studyEssentialResults && studyEssentialResults.errors) {
            return dispatch(addApplicationMessage('error', 'Failed to fetch study essentials'));
        }
        const studyEssentials = studyEssentialResults.data.studyDescriptors;
        dispatch({
            type: LOAD_STUDY_METADATA,
            payload: {
                institutionId,
                binderTemplate,
                descriptors,
                studyEssentials
            },
        });
        dispatch(studyStateLoadedAction());
    };
}

export function getBinderTemplateRequirements(institutionId, binderTemplateId) {
    const binderTemplateQuery = node('me', [
        nodeWithArgs('binderTemplate', {
            id: binderTemplateId,
        }, [
            'id',
            'displayName',
            node('rootQuery')
        ])
    ]);

    const descriptorQuery = nodeWithArgs('descriptors', {
        institutionId
    }, [
        'id',
        'displayName',
        'dataType',
        'label',
        'instructions',
    ]);
    return queryGraphQL(toQuery([binderTemplateQuery, descriptorQuery]));
}

const createStudyMutation = (state) => {
    const institutionId = userDefaultInstitutionIdSelector(state);
    const name = lodash.get(state, 'study.fields.studyName');
    const studyTypeId = lodash.get(state, 'study.fields.studyType.id');
    const currentStageId = lodash.get(state, 'study.fields.studyStage.id');

    const input = {
        name,
        institutionId,
        studyTypeId,
        currentStageId
    };

    const studyMutation = nodeWithArgs('createStudy', {
        institutionId,
        input
    }, [
        'id',
        'institutionId',
        'name',
        'studyTypeId',
        'currentStageId'
    ]);
    return mutateGraphQL(toMutation(studyMutation));
};

const createStudyTeamMembers = (state, studyId, dispatch) => {
    const institutionId = userDefaultInstitutionIdSelector(state);
    const studyTeam = lodash.get(state, 'study.fields.studyTeam', []);

    if (lodash.isEmpty(studyTeam)) {
        return;
    }

    const newStudyTeams = studyTeam.map(user => {
        return {
            studyId,
            institutionId,
            userId: user.userId,
            studyRoleId: user.studyRoleId,
            nameOfResearcher: user.nameOfResearcher,
            isClinicalStaff: false,
            studyUserType: STUDY_USER_TYPE.TEAM
        };
    });

    const studyTeamMutation = nodeWithArgs('createStudyTeamMembers', {
        institutionId,
        studyId,
        studyTeamMembers: newStudyTeams
    }, [
        'institutionId',
        'nameOfResearcher',
        'userId',
        'studyId',
        'isClinicalStaff',
        'studyUserType'
    ]);

    const createdStudyTeam = mutateGraphQL(toMutation(studyTeamMutation));
    validateResolvedMutation(createdStudyTeam, 'team members', dispatch);
};

const setStudyTeamBinderAccess = (state, binderId) => {
    const institutionId = lodash.get(state, 'study.institutionId', userDefaultInstitutionIdSelector(state));
    const studyTeam = lodash.get(state, 'study.fields.studyTeam', []);
    if (lodash.isEmpty(studyTeam)) {
        return;
    }
    const usersAtInstitution = lodash.get(state, 'study.teamMembers', []);
    const studyTeamPermissions = studyTeam.reduce((acc, studyTeamMember) => {
        if (studyTeamMember.sub) {
            return lodash.set(acc, `${studyTeamMember.sub}.hasBinderAccess`, 'yes');
        }

        /**
         * A users sub is needed to give binder access but study team members
         * associate a user by id. For existing study team members, look up their 
         * associated user by id among the users at an institution.
         */
        const matchingUser = lodash.find(usersAtInstitution, ['id', studyTeamMember.id]);
        return lodash.set(acc, `${matchingUser.sub}.hasBinderAccess`, 'yes');
    }, {});


    const studyTeamAccessMutation = nodeWithArgs('updateUserBinderPermissions', {
        userPermissions: {
            binderId,
            institutionId,
            permissions: JSON.stringify(studyTeamPermissions)
        }
    }, [
        'id',
        'sub',
        'displayName'
    ]);
    return mutateGraphQL(toMutation(studyTeamAccessMutation));
};

const createStudyBinder = (state, studyId) => {
    const institutionId = userDefaultInstitutionIdSelector(state);
    const title = lodash.get(state, 'study.fields.studyName');
    const binderTemplateId = lodash.get(state, 'study.binderTemplate.id');
    const metadata = lodash.get(state, 'study.fields.studyDetails');

    const studyInputWithValues = lodash.filter(metadata, metadata => !lodash.isEmpty(metadata.value));

    if (!studyInputWithValues) {
        return;
    }
    // Separates metadata values that were combined in the workflow
    const studyMetadata = lodash.flatMap(studyInputWithValues, ({ descriptorId, value, inputFieldType, dataType }) => {
        if (Array.isArray(value)) {
            return value.map(userInput => {
                return {
                    value: formatMetadataValue(inputFieldType, dataType, userInput),
                    descriptorId,
                };
            });
        }
        return {
            value: formatMetadataValue(inputFieldType, dataType, value),
            descriptorId,
        };
    });

    const studyBinderMutation = nodeWithArgs('createStudyBinder', {
        institutionId,
        title,
        binderTemplateId,
        studyMetadata,
        studyId,
    });
    return mutateGraphQL(toMutation(studyBinderMutation));
};

function isEmptyDetails(state) {
    const details = lodash.get(state, 'study.fields.studyDetails', []);

    if(details.length === 0) {
        return true;
    }
    return details.some(detail => !detail.value);
}

const updateStudy = (state, studyId, binderId) => {
    const institutionId = userDefaultInstitutionIdSelector(state);
    const studyName = lodash.get(state, 'study.fields.studyName');
    const studyTypeId = lodash.get(state, 'study.fields.studyType.id');
    const currentStageId = lodash.get(state, 'study.fields.studyStage.id');
    const eventsTabEnabled = lodash.get(state, 'study.fields.eventsTabEnabled', true);
    const delegationOfAuthorityEnabled = lodash.get(state, 'study.fields.delegationOfAuthorityEnabled', true);
    const isMissingDetail = isEmptyDetails(state);
    const input = {
        id: studyId,
        institutionId,
        name: studyName,
        binderId,
        studyTypeId,
        currentStageId,
        eventsTabEnabled,
        delegationOfAuthorityEnabled
    };
    const updateBinderMutation = nodeWithArgs('updateStudy', {
        institutionId,
        isMissingDetail,
        input
    }, [
        'id',
        'institutionId',
        'name',
        'binderId',
        'studyTypeId',
        'currentStageId',
        'delegationOfAuthorityEnabled'
    ]);
    return mutateGraphQL(toMutation(updateBinderMutation));
};

const createStudyMetadata = (state, studyId, type, dispatch) => {
    const institutionId = userDefaultInstitutionIdSelector(state);
    const studyInput = lodash.get(state, `study.fields.${TYPE[type]}`);
    const studyInputWithValues = lodash.filter(
        studyInput,
        metadata => !lodash.isEmpty(metadata.value) || !lodash.isNil(metadata.value));
    if (!studyInputWithValues) {
        return;
    }

    const metadata = lodash.map(studyInputWithValues, ({ name, value, inputFieldType, dataType, descriptorId }) => {
        if (Array.isArray(value)) {
            return value.map(userInput => {
                return {
                    name,
                    value: formatMetadataValue(inputFieldType, dataType, userInput),
                    inputFieldType,
                    type,
                    dataType,
                    institutionId,
                    descriptorId
                };
            });
        } else {
            return {
                name,
                value: formatMetadataValue(inputFieldType, dataType, value),
                inputFieldType,
                type,
                dataType,
                institutionId,
                descriptorId
            };
        }
    });

    const studyMetadataMutation = nodeWithArgs('createStudyMetadata', {
        institutionId,
        studyId,
        input: lodash.flatten(metadata)
    }, [
        'id',
        'name',
        'value',
        'inputFieldType',
        'type',
        'dataType',
        'studyId',
        'institutionId',
        'descriptorId'
    ]);

    const createdStudyMetadata = mutateGraphQL(toMutation(studyMetadataMutation));
    validateResolvedMutation(createdStudyMetadata, 'team members', dispatch);
};

export function getStudyStages() {
    return async (dispatch, getState) => {
        const storeState = getState();
        const institutionId = lodash.get(storeState, 'me.institution.id');
        const studyStagesQuery = nodeWithArgs('getStudyStages', {
            institutionId
        }, [
            'id',
            'name',
            'description',
            'institutionId',
            'createdAt',
            'updatedAt',
        ]);

        const response = await queryGraphQL(toQuery(studyStagesQuery));
        if (response.errors) {
            return dispatch(addApplicationMessage('error', 'Failed to load study stages. Please try again'));
        }
        else {
            return dispatch({
                type: LOAD_STUDY_STAGES,
                payload: response.data.getStudyStages
            });
        }
    };
}

export function clearStudy() {
    return dispatch => {
        dispatch({
            type: CLEAR_STUDY,
        });
    };
}

export function discardStudy(message) {
    return dispatch => {
        dispatch(addApplicationMessage('success', message));
        browserHistory.push('/');
    };
}

export function loadingStudyStateAction() {
    return {
        type: LOADING_STUDY_STATE,
    };
}

export function studyStateLoadedAction() {
    return {
        type: STUDY_STATE_LOADED,
    };
}

export function savingStudyStateAction() {
    return {
        type: SAVING_STUDY_STATE,
    };
}

export function studyStateSavedAction() {
    return {
        type: STUDY_STATE_SAVED
    };
}

export function getDataSourceIdsFromBinderTemplate(binderTemplate) {
    return lodash(Array.from(binderTemplate.predicateGroups.values()))
        .map(pg => pg.key.value.dataSourceId)
        .compact()
        .value();
}
